import React, { FC } from 'react';
import {
  alpha,
  Box,
  Button,
  ButtonBase,
  Card,
  CardProps,
  Divider,
  Stack,
  styled,
  SvgIcon,
  Typography
} from '@mui/material';
import * as yup from 'yup';
import { Formik, useFormik } from 'formik';
import BazaarTextField from 'components/BazaarTextField';
import EyeToggleButton from './EyeToggleButton';
import { FlexBox, FlexRowCenter } from 'components/flex-box';
import { LoadingButton } from '@mui/lab';
import { useAuthContext } from 'contexts/AuthContext';
import { useRouter } from 'next/router';
import SEO from 'components/SEO';
import Head from 'next/head';
import { isFunction } from 'lodash';
import { NaraCellarLogo } from '../../components/icons';
import dayjs from 'dayjs';
import Script from 'next/script';

const kakaoStyle = { background: '#FEE500', color: alpha('#000000', 0.85) };
const appleStyle = { background: 'black', color: 'white' };

type WrapperProps = { passwordVisibility?: boolean };

export const Wrapper = styled<FC<WrapperProps & CardProps>>(({ children, passwordVisibility, ...rest }) => (
  <Card {...rest}>{children}</Card>
))<CardProps>(({ theme, passwordVisibility }) => ({
  width: 500,
  padding: '2rem 3rem',
  [theme.breakpoints.down('sm')]: { width: '100%', padding: '2rem 1rem' },
  '.passwordEye': {
    color: passwordVisibility ? theme.palette.grey[600] : theme.palette.grey[400]
  },
  '.kakaoButton': { ...kakaoStyle, '&:hover': kakaoStyle }
}));

type SignInProps = {
  onSuccess?: () => void | Promise<void>;
  targetUrl?: string | undefined; // 로그인 성공 후 돌아갈 페이지 URL
};

/**
 * 로그인 영역
 * @param onSuccess 로그인 성공 콜백
 * @param targetUrl 로그인 성공 후 넘어갈 페이지 URL
 * @constructor
 */
const SignIn: FC<SignInProps> = ({ onSuccess, targetUrl }: SignInProps) => {
  const [passwordVisibility, setPasswordVisibility] = React.useState<boolean>(false);

  const togglePasswordVisibility = React.useCallback((): void => {
    setPasswordVisibility((visible: boolean) => !visible);
  }, []);

  const { signInWithIdAndPassword, closeSignInModal } = useAuthContext();

  const router = useRouter();
  const saForm = React.useRef<HTMLFormElement>(null);

  const handleFormSubmit = async (values: any, { setSubmitting }): Promise<void> => {
    const result = await signInWithIdAndPassword(values.cust_id, values.cust_pw);
    console.log('# 로그인 결과', result);

    // 로그인 성공 -> 메인 페이지로 이동
    if (result.success) {
      if (isFunction(onSuccess)) {
        await onSuccess();
      } else {
        console.log('################## emailpassword onSuccess default');
      }
    } else {
      const { code } = result.error;
      let errorMessage: string = '로그인 중 오류가 발생했습니다.\r\n잠시 후 다시 시도해주세요';

      // 비밀번호 틀림 || 아이디 없음
      if (
        code === 'auth/wrong-password' ||
        code === 'auth/user-not-found' ||
        code === 'auth/invalid-login-credentials'
      ) {
        errorMessage = '아이디 또는 비밀번호가 맞지 않습니다.';
      } else if (code === 'auth/internal-error') {
        errorMessage = '로그인 중 오류가 발생했습니다.\r\n잠시 후 다시 시도해주세요\r\n[인증서버 오류]';
      } else {
        console.warn('이메일 로그인 실패코드 error.code: ', code);
      }

      window.alert(errorMessage);
    }

    setSubmitting(false);
  };
  const handleCertFormSubmit = async (values): Promise<any> => {
    console.debug('[아이디/비밀번호 찾기] 본인 인증하기 버튼 클릭됨');

    // console.log('------------------------>window.MCASH_PAYMENT', window.MCASH_PAYMENT);

    try {
      // 본인인증화면 요청
      window.MCASH_PAYMENT(saForm.current);
    } catch (e) {
      console.error('아이디/비밀번호 찾기 본인인증 모듈 에러', e);
      window.alert('본인인증 모듈이 아직 준비되지 않았습니다.');
    }
  };

  const handleKakao = () => {
    if (!window.Kakao.isInitialized()) {
      window.alert('아직 카카오 모듈이 준비되지 않았습니다.');
      return;
    }
    const { Kakao } = window;
    // https://developers.kakao.com/sdk/reference/js/release/Kakao.Auth.html#.authorize
    Kakao.Auth.authorize({
      redirectUri: `${window.origin}/auth/sign-in/kakao/callback`,
      prompt: 'select_account',
      throughTalk: true,
      state: JSON.stringify({ targetUrl })
    });
  };

  const { values, errors, touched, handleBlur, handleChange, handleSubmit, isSubmitting } = useFormik({
    initialValues,
    onSubmit: handleFormSubmit,
    validationSchema: formSchema
  });

  // 회원가입 페이지로 이동
  const moveToSignUpPage = React.useCallback(() => {
    closeSignInModal();
    const signUpPageUrl = '/auth/sign-up';

    const searchParams = new URLSearchParams();
    if (targetUrl && targetUrl !== '/my' && targetUrl !== '/my/sithdrawal') searchParams.set('targetUrl', targetUrl);

    const destination = `${signUpPageUrl}?${searchParams.toString()}`;
    console.log('[Sign-in] 회원가입 페이지로 이동.', destination);
    router.push(destination);
  }, []);

  const { origin, host, tradeId } = React.useMemo(() => {
    let _host = '',
      _origin = '';
    if (typeof window !== 'undefined') {
      const { host, origin } = window.location;
      _host = host;
      _origin = origin;
    }
    const tradeId = generateTradeId();

    return { origin: _origin, host: _host, tradeId };
  }, []);

  // render
  return (
    <Wrapper elevation={3} passwordVisibility={passwordVisibility} sx={{ padding: '56px 48px 48px' }}>
      <SEO title="NARA CELLAR 통합홈페이지" />
      <Head>
        <meta name="robots" content="noindex,nofollow" />
      </Head>

      <Script charSet="euc-kr" src={`${process.env.NEXT_PUBLIC_MCASH_SVR_DOMAIN}/js/ext/ext_inc_comm.js`} />

      <FlexBox justifyContent="center">
        <NaraCellarLogo />
      </FlexBox>

      <Button
        type="button"
        className="kakaoButton"
        size="medium"
        onClick={handleKakao}
        fullWidth
        sx={{ marginTop: '48px', height: 44 }}
      >
        <SvgIcon viewBox="0 0 48 48" sx={{ width: 48, height: 48 }}>
          <path
            d="M11.5 0C5.148 0 0 4.059 0 9.067c0 3.233 2.153 6.082 5.391 7.683-.176.607-1.132 3.908-1.17 4.168 0 0-.023.195.1.269a.347.347 0 0 0 .279.013c.36-.048 4.2-2.74 4.863-3.2a14.547 14.547 0 0 0 2.037.138c6.352 0 11.5-4.059 11.5-9.067S17.852 0 11.5 0z"
            transform="translate(12.5 14)"
            style={{ fill: '#3a2929' }}
          />
        </SvgIcon>
        <Box fontSize={14} ml={-0.5}>
          카카오로 시작하기
        </Box>
      </Button>

      <Box marginTop="20px">
        <FlexBox justifyContent="center">
          <Box width="200px" marginTop={1.2}>
            <Divider />
          </Box>
        </FlexBox>

        <FlexBox justifyContent="center" marginTop={-1.2}>
          <Box color="#7D879C" fontSize={14} bgcolor="background.paper" px={2}>
            또는
          </Box>
        </FlexBox>
      </Box>

      <form onSubmit={handleSubmit}>
        <BazaarTextField
          mt="20px"
          mb={1.5}
          fullWidth
          name="cust_id"
          size="small"
          type="text"
          variant="outlined"
          onBlur={handleBlur}
          value={values.cust_id}
          onChange={handleChange}
          label="아이디"
          placeholder="아이디"
          error={!!touched.cust_id && !!errors.cust_id}
          helperText={(touched.cust_id && errors.cust_id) as string}
        />

        <BazaarTextField
          fullWidth
          size="small"
          name="cust_pw"
          label="비밀번호"
          autoComplete="on"
          variant="outlined"
          onBlur={handleBlur}
          onChange={handleChange}
          value={values.password}
          placeholder="*********"
          type={passwordVisibility ? 'text' : 'password'}
          error={!!touched.cust_pw && !!errors.cust_pw}
          helperText={(touched.cust_pw && errors.cust_pw) as string}
          InputProps={{
            endAdornment: <EyeToggleButton show={passwordVisibility} click={togglePasswordVisibility} />
          }}
        />

        {/*로그인 버튼*/}
        <LoadingButton
          fullWidth
          loading={isSubmitting}
          type="submit"
          variant="contained"
          sx={{
            marginTop: '20px',
            fontSize: '14px',
            fontWeight: 700,
            color: '#FFF',
            backgroundColor: '#909842',
            height: 44
          }}
        >
          로그인
        </LoadingButton>
      </form>
      <Formik onSubmit={handleCertFormSubmit} initialValues={{}}>
        {({ handleSubmit }) => {
          return (
            <form ref={saForm} name="saForm" acceptCharset="utf-8" onSubmit={handleSubmit}>
              <Box display="none">
                <input type="text" name="CASH_GB" id="CASH_GB" value="CI" readOnly /> {/* 고정 */}
                <input type="text" name="CI_SVCID" value={process.env.NEXT_PUBLIC_MCASH_CI_SVCID} readOnly />
                <input type="text" name="PAY_MODE" value={'10'} readOnly />
                <input type="text" name="Okurl" value={`${origin}/api/cert/callback`} readOnly />
                <input type="text" name="Cryptyn" value="Y" readOnly />
                <input type="text" name="Keygb" value="0" readOnly />
                {/*<input type="text" name="Siteurl" value={host} readOnly />*/}
                <input type="text" name="Siteurl" value="naramembership.com" readOnly />
                <input type="text" name="Tradeid" value={tradeId} readOnly />
                <input type="text" name="LOGO_YN" value="Y" readOnly />
                <input type="text" name="CI_Mode" value="61" readOnly />
                <input readOnly type="text" name="MSTR" value={`findid`} />
                <input type="text" name="CALL_TYPE" value="SELF" readOnly />
              </Box>

              <FlexRowCenter mt="28px">
                <Stack
                  direction="row"
                  divider={
                    <Box py="8px" height="28px" display="flex">
                      <Divider orientation="vertical" flexItem />
                    </Box>
                  }
                  spacing={1}
                  justifyContent="center"
                >
                  <ButtonBase onClick={moveToSignUpPage}>
                    <Typography variant="caption" color="#4B566B" fontSize={12}>
                      회원가입
                    </Typography>
                  </ButtonBase>
                  <ButtonBase type="submit">
                    <Typography variant="caption" color="#4B566B" fontSize={12}>
                      아이디 찾기/비밀번호 재설정
                    </Typography>
                  </ButtonBase>
                </Stack>
              </FlexRowCenter>
            </form>
          );
        }}
      </Formik>
    </Wrapper>
  );
};

const initialValues = { cust_id: '', cust_pw: '' };

const formSchema = yup.object().shape({
  cust_id: yup.string().required('아이디를 입력해주세요'),
  cust_pw: yup.string().required('비밀번호를 입력해주세요')
});

export const getServerSideProps = async () => {
  return { props: { tradeId: generateTradeId() } };
};

function generateTradeId() {
  const now = dayjs();
  // I generate the UID from two parts here
  // to ensure the random number provide enough bits.
  let firstPart: number | string = (Math.random() * 46656) | 0;
  let secondPart: number | string = (Math.random() * 46656) | 0;
  firstPart = ('000' + firstPart.toString(36)).slice(-3);
  secondPart = ('000' + secondPart.toString(36)).slice(-3);
  return `membership-${now.format('YYMMDDHHmmss')}-${firstPart}${secondPart}`;
}

export default SignIn;
